import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, RefreshCw } from 'lucide-react';
import { STRATEGIES, suggestPrefix } from '../config/codeStrategies';
import ReusableDropdown from '../../../components/ReusableDropdown';
import { apiFetch } from '../../../context/AuthContext';
import { text } from '../constants/configurationText';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AddCompanyModal = ({ theme, styles, t = text.en, onAdd, onClose }) => {
  const [newCompanyName, setNewCompanyName] = useState('');
  const [maxEmployees, setMaxEmployees] = useState('50');
  const [maxUsers, setMaxUsers] = useState('5');
  const [adding, setAdding] = useState(false);

  const [prefix, setPrefix] = useState('');
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [strategy, setStrategy] = useState('SEQUENTIAL');
  const [bodyLength, setBodyLength] = useState(4);
  const [prefixError, setPrefixError] = useState('');
  const [previewCodes, setPreviewCodes] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const debounceRef = useRef(null);

  const labelStyle = styles.modalLabel;

  // Auto-suggest a prefix from the company name until the admin edits the
  // prefix field by hand — after that, typing the name no longer overrides
  // their choice.
  useEffect(() => {
    if (!prefixTouched) {
      setPrefix(suggestPrefix(newCompanyName));
    }
  }, [newCompanyName, prefixTouched]);

  // Debounced live preview against the real backend renderer — no DB
  // writes, works even though this client doesn't exist yet.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!prefix.trim()) {
      setPreviewCodes([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const response = await apiFetch(`${BASE_URL}/api/configurations/code-policy/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prefix,
            separator: '-',
            strategy,
            bodyLength,
            padWithZeros: true,
            startAt: 1,
            casing: 'UPPER',
            count: 3,
          }),
        });
        const data = await response.json();
        setPreviewCodes(response.ok ? (data?.data?.codes || []) : []);
      } catch {
        setPreviewCodes([]);
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [prefix, strategy, bodyLength]);

  const handleAdd = async () => {
    const trimmedPrefix = prefix.trim().toUpperCase();
    if (!trimmedPrefix) {
      setPrefixError(t.prefixRequired);
      return;
    }
    if (!/^[A-Za-z0-9]{1,12}$/.test(trimmedPrefix)) {
      setPrefixError(t.prefixInvalid);
      return;
    }
    setPrefixError('');
    setAdding(true);
    const success = await onAdd(newCompanyName, {
      maxEmployees: maxEmployees === '' ? null : Number(maxEmployees),
      maxUsers: maxUsers === '' ? null : Number(maxUsers),
      codePolicy: {
        prefix: trimmedPrefix,
        separator: '-',
        strategy,
        bodyLength,
        padWithZeros: true,
        startAt: 1,
        casing: 'UPPER',
      },
    });
    setAdding(false);
    if (success) {
      setNewCompanyName('');
      setMaxEmployees('50');
      setMaxUsers('5');
      setPrefix('');
      setPrefixTouched(false);
      setStrategy('SEQUENTIAL');
      setBodyLength(4);
      onClose();
    }
  };

  return createPortal(
    <div style={styles.modal} onClick={onClose}>
      <div className="cfg-modal" style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{t.addTitle}</h3>
          <button style={styles.closeButton} onClick={onClose} aria-label={t.close}>
            <X size={18} />
          </button>
        </div>

        <div>
          <label style={labelStyle}>
            {t.companyName}
          </label>
          <input
            type="text"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            placeholder={t.companyNamePlaceholder}
            className="cfg-input"
            style={styles.input}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !adding) handleAdd();
            }}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: "16px" }}>
          <div>
            <label style={labelStyle}>{t.codePrefix}</label>
            <input
              type="text"
              value={prefix}
              onChange={(e) => {
                setPrefixTouched(true);
                setPrefix(e.target.value.toUpperCase());
                setPrefixError('');
              }}
              placeholder="RAY"
              maxLength={12}
              className="cfg-input"
              style={{ ...styles.input, marginBottom: prefixError ? '6px' : '20px' }}
            />
            {prefixError && (
              <p style={{ fontSize: '12px', color: '#ef4444', fontWeight: 600, margin: '0 0 20px' }}>{prefixError}</p>
            )}
          </div>
          <div>
            <label style={labelStyle}>{t.codeFormat}</label>
            <ReusableDropdown
              items={STRATEGIES}
              value={strategy}
              onChange={setStrategy}
              getItemKey={(s) => s.value}
              getItemLabel={(s) => t.strategies[s.value]?.label || s.label}
              searchable={false}
              theme={theme === 'light' ? 'light' : 'dark'}
              language={t.lang}
              inputStyle={styles.field}
            />
          </div>
        </div>

        <div style={{
          background: styles.colors.chipBg,
          border: '1px solid rgba(46,124,186,0.25)',
          borderRadius: '12px',
          padding: '14px',
          marginTop: '-4px',
          marginBottom: '22px',
        }}>
          <div style={{ ...labelStyle, color: styles.colors.chipText, marginBottom: '10px' }}>
            {t.nextCodes}
          </div>
          {previewLoading ? (
            <span style={{ fontSize: '12px', opacity: 0.6 }}>{t.generating}</span>
          ) : previewCodes.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {previewCodes.map((code, i) => (
                <code key={i} style={styles.codeChip}>
                  {code}
                </code>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '12px', opacity: 0.5 }}>{t.enterPrefixShort}</span>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: "16px" }}>
          <div>
            <label style={labelStyle}>{t.dataQuota}</label>
            <input
              type="number"
              min="0"
              value={maxEmployees}
              onChange={(e) => setMaxEmployees(e.target.value)}
              placeholder={t.blankUnlimited}
              className="cfg-input"
              style={{ ...styles.input, marginBottom: 0 }}
            />
            <p style={styles.hint}>
              {t.dataQuotaHint}
            </p>
          </div>
          <div>
            <label style={labelStyle}>{t.accountQuota}</label>
            <input
              type="number"
              min="0"
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value)}
              placeholder={t.blankUnlimited}
              className="cfg-input"
              style={{ ...styles.input, marginBottom: 0 }}
            />
            <p style={styles.hint}>
              {t.accountQuotaHint}
            </p>
          </div>
        </div>

        <div style={styles.modalButtons}>
          <button className="cfg-btn-secondary" style={styles.btnSecondary(adding)} onClick={onClose} disabled={adding}>
            {t.cancel}
          </button>
          <button className="cfg-btn-primary" style={styles.btnPrimary(adding)} onClick={handleAdd} disabled={adding}>
            {adding ? (
              <>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                {t.creating}
              </>
            ) : (
              <>
                <Plus size={16} />
                {t.createCompany}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddCompanyModal;
